import React from "react"
import { Box, Button, Grid, Typography, Stack, Divider, Chip } from "@mui/material"
import { FaChevronLeft, FaCheck } from "react-icons/fa6"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useGetJobServiceQuery } from "../../features/OverViewAuth/overViewApi"
import { useAppDispatch } from "../../app/store"
import * as Style from "./style"
import { offersSelect } from "../../features/OverViewAuth/overViewSlice"
import CenteredSpinner from "../common/CenteredSpinner"

const SingleFeature = () => {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    data: getService,
    isFetching,
    isLoading,
  } = useGetJobServiceQuery({ id })

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  return (
    <>
      <Style.Overview>
        {isFetching || isLoading ? (
          <CenteredSpinner />
        ) : (
          <Box sx={{ px: 2, py: 3 }}>
            <Button
              onClick={() => navigate(`/dashboard/features`)}
              className="back-btn "
            >
              <FaChevronLeft />
            </Button>

            <Grid container justifyContent="center">
              <Grid item xs={12} md={10} lg={8}>
                {getService?.data?.map((item: any) => (
                  <Box
                    key={item?.id}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 3,
                      boxShadow: 3,
                      p: 2,
                      position: "relative",
                      bgcolor: "#fff",
                      mb: 3,
                    }}
                  >
                    {/* Book Now Button - Top Right */}
                    {/* Salon bookings are temporarily frozen: keep the service
                        visible but disable the booking action. */}
                    {(() => {
                      const isSalonFrozen = /salon/i.test(item?.name || "")
                      return (
                        <Button
                          disabled={isSalonFrozen}
                          onClick={() => {
                            if (isSalonFrozen) return
                            dispatch(offersSelect(item?.name))
                            navigate(`/dashboard/create-booking?serviceId=${item?._id}&serviceName=${encodeURIComponent(item?.name)}`)
                          }}
                          sx={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            backgroundColor: "#f475A4",
                            color: "#fff",
                            textTransform: "capitalize",
                            px: 3,
                            py: 1,
                            border: "1px solid #f475A4",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "#f475A4",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#e0e0e0",
                              color: "#9e9e9e",
                              border: "1px solid #e0e0e0",
                            },
                          }}
                        >
                          {isSalonFrozen ? "Coming Soon" : "Book Now"}
                        </Button>
                      )
                    })()}

                    {/* Image Centered */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 3,
                        mt: 6,
                        "& img": {
                          width: "100%",
                          maxWidth: "480px",
                          height: "auto",
                          borderRadius: "12px",
                          objectFit: "cover",
                          "@media (max-width: 600px)": {
                            maxWidth: "100%",
                          },
                        },
                      }}
                    >
                      <img src={item?.image} alt="service" />
                    </Box>


                    {/* Service Name - Removed border */}
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", mt: 1, textAlign: "center", mb: 3 }}
                    >
                      {item?.name}
                    </Typography>

                    {/* Description */}
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        p: 2,
                        mb: 3,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {item?.description?.split("\n").map((line: string, index: number) => (
                          <React.Fragment key={index}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                      </Typography>
                    </Box>

                    {/* Charges */}
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        p: 2,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                        Charges
                      </Typography>

                      {/* Sub-Modality Pricing (e.g. Cooking types) */}
                      {item?.pricingConfiguration?.subModality && (
                        <Box sx={{ mb: 2 }}>
                          {Object.entries(item.pricingConfiguration.subModality).map(
                            ([label, value]: any) => {
                              const isObject = typeof value === "object" && value !== null
                              const base = isObject ? value.base : value
                              const extra = isObject ? value.extra : 0
                              const includedServices = isObject ? value.includedServices : null
                              return (
                                <Box key={label} sx={{ mb: 1.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {label}
                                  </Typography>
                                  {isObject ? (
                                    <>
                                      <Typography variant="body2" sx={{ color: "#f475A4", ml: 1 }}>
                                        • C$ {base}/Hr{Number(extra) > 0 ? ` + C$ ${extra}/Hr` : ''}. Per Person
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography variant="body2" sx={{ color: "#f475A4", ml: 1 }}>
                                      • C$ {base}/Hr. Per Person
                                    </Typography>
                                  )}
                                  {includedServices && includedServices.length > 0 && (
                                    <Box sx={{ ml: 1, mt: 0.5 }}>
                                      {includedServices.map((svc: any, i: number) => (
                                        <Typography key={i} variant="caption" display="block" sx={{ color: "#666" }}>
                                          <FaCheck style={{ fontSize: "0.6rem", marginRight: 4, color: "#4caf50" }} />
                                          {svc.name} — C$ {svc.individualPrice?.toLocaleString()}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )
                            }
                          )}
                        </Box>
                      )}

                      {/* Default Pricing (Fallback) */}
                      {!item?.pricingConfiguration && (
                        <Typography variant="body1" sx={{ color: "#f475A4" }}>
                          C$ {item?.price}/Hr. Per Person
                        </Typography>
                      )}
                    </Box>

                  </Box>
                ))}
              </Grid>
            </Grid>
          </Box>
        )}
      </Style.Overview>
    </>
  )
}

export default SingleFeature
